import type React from "react";
import type { AppSlug } from "@app/portal-sdk";
import { bkdp } from "@app/ui-kit";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PriceChangeOutlined from "@mui/icons-material/PriceChangeOutlined";
import RecordVoiceOverOutlined from "@mui/icons-material/RecordVoiceOverOutlined";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";

/**
 * MNT-1: one thing BKDP maintains for an application and every client reads. `maintainedBy` says how it is maintained
 * today: through a screen in the admin console (the shell mounts admin/apps/tabs.tsx's component for the key), or
 * somewhere else (`elsewhere` says where and what changing it requires). The second is a first-class state, not an
 * omission: a screen that does not exist is admitted, never implied.
 */
export type MaintainedThing = { key: string; label: string; description: string } & ({ maintainedBy: "screen" } | { maintainedBy: "elsewhere"; elsewhere: string });
/** MNT-1: what BKDP maintains for an application, in one sentence that also says who reads it, and the things themselves. */
export type AppMaintenance = { sentence: string; things: MaintainedThing[] };

export type AppDefinition = {
  slug: AppSlug;
  name: string;
  /** Left-nav wording when it differs from the name (DESIGN Section 11: "Customers" for Customer Management). */
  navLabel?: string;
  description: string;
  load: () => Promise<{ default: React.ComponentType }>;
  icon?: React.ElementType;
  iconColor?: string;
  /**
   * MNT-1: declared here and nowhere else. The admin console's Applications section, the index at /admin/apps, the
   * maintenance screen and its tabs are all rendered from this; an application with nothing to maintain leaves it
   * out and never appears there (STORIES_APP_MAINTENANCE.md, decisions 2 and 3).
   */
  maintained?: AppMaintenance;
};

/**
 * Every client application the host can render. Keys must match
 * APP_SLUGS in packages/portal-sdk/src/types.ts.
 */
export const APP_REGISTRY: Record<AppSlug, AppDefinition> = {
  "example-notes": {
    slug: "example-notes",
    name: "Notes",
    description: "Reference client application: client-scoped notes.",
    load: () => import("./example-notes/App"),
    icon: StickyNote2Outlined,
    iconColor: bkdp.slate
  },
  "channel-analytics": {
    slug: "channel-analytics",
    name: "Channel Analytics",
    description: "What each of your channels actually earns, and what to do about the ones that do not.",
    load: () => import("./channel-analytics/App"),
    icon: InsightsOutlined,
    iconColor: bkdp.signalBlue,
    // WBX-4: the filled example workbook is BKDP's, at the portal level; it left the client's Templates card (WBX-1).
    maintained: {
      sentence: "The example workbook every client may be shown: a filled collection workbook for the invented demo client, kept here and served from no client screen.",
      things: [
        { key: "example", label: "Example workbook", description: "A filled collection workbook for BKDP Anchor & Fastener, the invented demo client, to show a client what a completed workbook looks like.", maintainedBy: "screen" }
      ]
    }
  },
  // WORD-1: the slug, the module path, the docs folder, the audit actions and the display name all say customer management.
  "customer-management": {
    slug: "customer-management",
    name: "Customer Management",
    navLabel: "Customer Management",
    description: "Your organizations, the people in them, and every interaction.",
    load: () => import("./customer-management/App"),
    icon: GroupsOutlined,
    iconColor: bkdp.teal
  },
  // DESIGN.md Section 0.1: the one application a client administrator may write to, deliberately.
  "voice-of-customer": {
    slug: "voice-of-customer",
    name: "Voice of Customer",
    description: "What your customers said, what it told you, and what you agreed to do about it.",
    load: () => import("./voice-of-customer/App"),
    icon: RecordVoiceOverOutlined,
    iconColor: bkdp.plum,
    // MNT-5: the question library has no screen; that is said here rather than implied by a screen nobody can find.
    maintained: {
      sentence: "BKDP maintains the question library and the application's lists. Every client's studies start from them.",
      things: [
        { key: "library", label: "Question library", description: "Sixty questions in their categories, seeded to every client entitled to the application; shown here read-only.", maintainedBy: "elsewhere",
          elsewhere: "A file in this repository: apps/backend-api/assets/voice-of-customer/question-library.json. Changing it needs a commit, a deploy, and RESEED_REFERENCE on the environment before clients that already hold the library see the change (decision 89's boundary). There is no screen that edits it; giving it one is an epic of its own, because it needs the library's promote, retire and relabel semantics." },
        { key: "lists", label: "Lists", description: "The application's own lists: study, participant, meeting, finding, action and objective statuses, question types, scope dimensions, confidentiality, participant priority.", maintainedBy: "elsewhere",
          elsewhere: "A file in this repository: apps/backend-api/assets/voice-of-customer/reference-seed.json, seeded per client, and each client relabels its own copy on its Reference data screen. Changing the seed needs a commit, a deploy, and RESEED_REFERENCE; a reseed never overwrites a client's own words." }
      ]
    }
  },
  // CORE-9: the client's channels as entities in schema core; Channel Analytics reads their projection.
  "commercial-model": {
    slug: "commercial-model",
    name: "Commercial Model",
    description: "Your channels: what each one is, who owns it, and who trades through it.",
    load: () => import("./commercial-model/App"),
    icon: AccountTreeOutlined,
    iconColor: bkdp.sage
  },
  // PRD-1: the product subject area, split out of Commercial Model on 2026-09-14 for depth - hierarchy,
  // lifecycle, identifiers and assortment each want their own navigation.
  "product-management": {
    slug: "product-management",
    name: "Product Management",
    description: "The products you sell, their brands, their identifiers in every system, and where each one is carried.",
    load: () => import("./product-management/App"),
    icon: Inventory2Outlined,
    iconColor: bkdp.steelBlue
  },
  // SHP-1: what our products cost everywhere they are sold. Observes; never changes a price, never contacts a seller.
  "price-shopper": {
    slug: "price-shopper",
    name: "Price Shopper",
    description: "What your products cost everywhere they are sold, observed on a schedule, and what that means.",
    load: () => import("./price-shopper/App"),
    icon: PriceChangeOutlined,
    iconColor: bkdp.amber
  },
  // DDE-1: buy-side diligence. The client runs the engagement (DESIGN.md Section 3, answer 4); access is per engagement (Section 6).
  "due-diligence": {
    slug: "due-diligence",
    name: "Due Diligence",
    description: "Buy-side diligence on a company you are considering buying.",
    load: () => import("./due-diligence/App"),
    icon: GavelOutlined,
    iconColor: bkdp.deepNavy,
    // MNT-1, HIE-10: the library and the queue belong to no client; every client's engagement starts from the library.
    maintained: {
      sentence: "BKDP maintains the library and the queue of proposed questions. Every client's engagement starts from the library.",
      things: [
        { key: "library", label: "Library", description: "The functional areas, review areas and questions every client's engagement starts from.", maintainedBy: "screen" },
        { key: "queue", label: "Proposed questions", description: "Questions clients have offered for the library, across every client, accepted or declined here.", maintainedBy: "screen" },
        // The seed file as a tool, not a sign (2026-10-14): the library goes down as it stands, a changed file comes back through check and commit,
        // and the tab says whether this environment's library still matches the repository's file, with the export for when it does not.
        { key: "seed", label: "Seed file", description: "The library as a file: download it as it stands, load a changed file through check and commit, and export it for the repository when this environment's library has moved on from the file it was seeded from.", maintainedBy: "screen" }
      ]
    }
  }
};

export const APP_DEFINITIONS: AppDefinition[] = Object.values(APP_REGISTRY);

export const isAppSlug = (value: string): value is AppSlug => value in APP_REGISTRY;
